import { booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input } from '@angular/core';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { FileSizePipe } from '../file-size.pipe';
import { isValidFile, SelectedFile } from '../selected-file.model';

/**
 * Files input component.
 */
@Component({
  selector: 'app-files',
  templateUrl: './files.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxFileDropModule, FileSizePipe]
})
export class FilesComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  /**
  * Selected files.
  */
  public files = input.required<SelectedFile[]>();

  /**
   * Accepted file types.
   */
  public fileTypes = input.required({
    transform: (value: string[] | undefined) => value ?? []
  })

  /**
   * Accepted file types file input filter.
   */
  protected readonly accept = computed(() => this.fileTypes().join(','));

  /**
   * File(s) dropped event handler.
   * 
   * @param entries Dropped file(s)/directory(s).
   */
  protected dropped(entries: NgxFileDropEntry[]): void {
    for (const entry of entries) {
      if (entry.fileEntry.isFile) {
        const fileEntry = entry.fileEntry as FileSystemFileEntry;
        fileEntry.file(file => {
          const fileModel = new SelectedFile(file);
          this.files().push(fileModel);
          this.cdr.markForCheck();
        });
      } else {
        console.warn('[Files D&D] Directory detected. Skip processing');
      }
    }
  }

  /**
   * Removes file from the list of selected files.
   * 
   * @param index Index ot the element in selected files array.
   */
  protected remove(index: number): void {
    this.files().splice(index, 1);
  }

  /**
   * Validates file.
   * 
   * @param file File to validate.
   * @returns True if file is valid, otherwise - false.
   */
  protected isValid(file: File): boolean {
    return isValidFile(file, this.fileTypes());
  }

  /**
   * Specifies if component is disabled.
   */
  public disabled = input(false, { transform: booleanAttribute });
}
